import dayjs from 'dayjs';

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];


export const DAY_STATUS = {
    INHERIT: 'inherit',
    NORMAL: 'normal',
    OFF: 'off',
    CUSTOM: 'custom',
    CUSTOM_OFF: 'customOff'
};

export const OFF_DAY_TIMES = { open: '00:00', close: '23:59' };

export const DATE_OVERRIDE_TYPE = {
    CLOSED: 'CLOSED',
    OPEN: 'OPEN'
};

export const SETTING_FIELDS = [
    { key: 'maxAdvanceDays', label: 'Max advance days', suffix: 'days', min: 1, max: 365, hidden: true, hint: 'How many days ahead customers can book. Empty = no limit.' },
    { key: 'minLeadMinutes', label: 'Min lead time', suffix: 'minutes', min: 0, max: 1440, hidden: true, hint: 'How long before a slot it can still be booked (0 to 1440).' },
    { key: 'slotMinutes', label: 'Slot duration', suffix: 'minutes', min: 1, hint: 'How long one appointment slot is. Changing it rebuilds slots, starting after the last booked day.' },
    { key: 'slotMaxSeats', label: 'Seats per slot', suffix: 'seats', min: 1, hint: 'How many people can book the same slot. Changing it updates current slots right away. At centers with counters, seats = the counters open at that time.' }
];

export const VISIBLE_SETTING_FIELDS = SETTING_FIELDS.filter(field => !field.hidden);


const isReadableText = (text) => typeof text === 'string' && text.trim() && text.length < 300 && !text.trim().startsWith('<');

export const getErrorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
    if (!err) return fallback;
    if (!err.response) {
        if (err.code === 'ECONNABORTED') return 'The request timed out. Please try again.';
        if (err.message === 'Network Error') return 'Unable to reach the server. Check your connection and try again.';
        return err.message || fallback;
    }
    const { status, data } = err.response;
    if (isReadableText(data)) return data;
    if (data && isReadableText(data.message)) return data.message;
    if (data && data.errors && typeof data.errors === 'object') {
        const first = Object.values(data.errors).flat().find(isReadableText);
        if (first) return first;
    }
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const first = Object.values(data).filter(Array.isArray).flat().find(isReadableText);
        if (first) return first;
    }
    if (data && isReadableText(data.title)) return data.title;
    if (status === 401) return 'Your session has expired. Please log in again.';
    if (status === 404) return 'The requested item was not found. It may have been deleted.';
    if (status >= 500) return 'Server error. Please try again in a moment.';
    return fallback;
};

const MESSAGE_FIELDS = [
    [/^MaxAdvanceDays/i, 'maxAdvanceDays'],
    [/^MinLeadMinutes/i, 'minLeadMinutes'],
    [/^SlotMinutes/i, 'slotMinutes'],
    [/^SlotMaxSeats/i, 'slotMaxSeats'],
    [/weekly schedule|listed more than once|Open and Close|Open time|Break/i, 'weeklySchedule'],
    [/^Title/i, 'title'],
    [/^Type must/i, 'type'],
    [/ToDate|yearly recurring/i, 'toDate'],
    [/FromTime|ToTime/i, 'time'],
    [/already exists/i, 'fromDate'],
    [/Center Not Found/i, 'centerId']
];

export const fieldFromMessage = (message) => {
    const match = MESSAGE_FIELDS.find(([pattern]) => pattern.test(message || ''));
    return match ? match[1] : null;
};


export const toTime = (value) => (value ? String(value).substring(0, 5) : '');

export const formatTime12 = (value) => {
    const time = toTime(value);
    if (!/^\d{1,2}:\d{2}$/.test(time)) return time;
    const [hours, minutes] = time.split(':').map(Number);
    return `${String(hours % 12 || 12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
};

export const formatTimeRange = (from, to) => `${formatTime12(from)} – ${formatTime12(to)}`;

export const formatTimeShort = (value) => {
    const time = toTime(value);
    if (!/^\d{1,2}:\d{2}$/.test(time)) return time;
    const [hours, minutes] = time.split(':').map(Number);
    return `${hours % 12 || 12}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''} ${hours >= 12 ? 'PM' : 'AM'}`;
};

export const toMinutes = (value) => {
    const time = toTime(value);
    if (!/^\d{1,2}:\d{2}$/.test(time)) return null;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

export const formatClosedRange = (range) => {
    const [from, to] = String(range || '').split('-');
    return to ? formatTimeRange(from.trim(), to.trim()) : range;
};

export const toNumberOrNull = (value) =>
    value === '' || value === null || value === undefined ? null : Number(value);

export const toInputValue = (value) => (value === null || value === undefined ? '' : String(value));

export const toCenterId = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

export const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '-');

export const formatDateTime = (value) => (value ? dayjs(value).format('DD MMM YYYY, hh:mm A') : '');

export const formatValue = (field, value) => {
    if (value === null || value === undefined || value === '') return field === 'maxAdvanceDays' ? 'No limit' : 'Not set';
    const config = SETTING_FIELDS.find(f => f.key === field);
    return config ? `${value} ${config.suffix}` : String(value);
};

export const normalizeCenters = (res) => {
    const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
    return list
        .map(c => ({
            value: c.centerCode ?? c.CenterCode,
            label: c.centerName || c.name || c.nameEn || c.centerNameEn || c.CenterName || String(c.centerCode ?? c.CenterCode)
        }))
        .filter(c => c.value !== null && c.value !== undefined);
};


const isDated = (entry) => !!(entry && (entry.effectiveFrom || entry.effectiveTo));

const findWeeklyRule = (schedule, day) => (schedule || []).find(d => d.day === day && !isDated(d));

const datedRulesFor = (schedule, day) =>
    (schedule || [])
        .filter(d => d.day === day && isDated(d))
        .sort((a, b) => (a.effectiveFrom || '').localeCompare(b.effectiveFrom || ''));

const statusOf = (entry) => {
    if (!entry) return null;
    if (entry.isOff) return DAY_STATUS.OFF;
    if (entry.open || entry.close) return DAY_STATUS.CUSTOM;
    return DAY_STATUS.NORMAL;
};

let rangeSeq = 0;
export const newRangeId = () => `range-${Date.now()}-${++rangeSeq}`;

export const emptyRange = () => ({ id: newRangeId(), from: '', to: '', status: DAY_STATUS.OFF, open: '', close: '' });
const breakOf = (entry) => ({
    hasBreak: !!(entry?.breakFrom || entry?.breakTo),
    breakFrom: toTime(entry?.breakFrom),
    breakTo: toTime(entry?.breakTo)
});

const toRange = (day, rule, index) => ({
    id: `${day}-${index}-${rule.effectiveFrom}`,
    from: rule.effectiveFrom || '',
    to: rule.effectiveTo || '',
    status: statusOf(rule),
    open: toTime(rule.open),
    close: toTime(rule.close),
    ...breakOf(rule)
});


export const scheduleToRows = (schedule, missingStatus = DAY_STATUS.NORMAL) =>
    DAYS.map(day => {
        const entry = findWeeklyRule(schedule, day);
        const dated = datedRulesFor(schedule, day);
        if (!entry && dated.length === 1 && dated[0].isOff) {
            return {
                day, status: DAY_STATUS.CUSTOM_OFF, open: '', close: '', ...breakOf(null),
                from: dated[0].effectiveFrom || '', to: dated[0].effectiveTo || '', ranges: []
            };
        }
        const status = statusOf(entry) || missingStatus;
        const isCustom = status === DAY_STATUS.CUSTOM;
        return {
            day,
            status,
            open: isCustom ? toTime(entry?.open) : '',
            close: isCustom ? toTime(entry?.close) : '',
            ...breakOf(isCustom ? entry : null),
            from: '',
            to: '',
            ranges: dated.map((rule, index) => toRange(day, rule, index))
        };
    });

const toApiRule = (day, rule) => {
    if (rule.status === DAY_STATUS.OFF || rule.status === DAY_STATUS.CUSTOM_OFF) return { day, isOff: true, ...OFF_DAY_TIMES };
    if (rule.status === DAY_STATUS.CUSTOM) {
        const api = { day, isOff: false, open: rule.open, close: rule.close };
        if (rule.hasBreak) Object.assign(api, { breakFrom: rule.breakFrom, breakTo: rule.breakTo });
        return api;
    }
    return { day, isOff: false };
};


export const rowsToSchedule = (rows, isCenter = false) =>
    rows.reduce((list, row) => {
        if (row.status === DAY_STATUS.CUSTOM_OFF) {
            list.push({ ...toApiRule(row.day, row), effectiveFrom: row.from, effectiveTo: row.to });
        } else if (row.status === DAY_STATUS.OFF || row.status === DAY_STATUS.CUSTOM || (row.status === DAY_STATUS.NORMAL && isCenter)) {
            list.push(toApiRule(row.day, row));
        }
        (row.ranges || []).forEach(range => {
            list.push({ ...toApiRule(row.day, range), effectiveFrom: range.from, effectiveTo: range.to });
        });
        return list;
    }, []);

export const describeScheduleDay = (schedule, day) => {
    const entry = findWeeklyRule(schedule, day);
    const status = statusOf(entry) || DAY_STATUS.NORMAL;
    const ranges = datedRulesFor(schedule, day).length;
    if (status === DAY_STATUS.OFF) return { kind: 'off', status, text: 'Off', ranges };
    if (status === DAY_STATUS.CUSTOM) {
        const breakText = entry.breakFrom && entry.breakTo ? ` (break ${formatTimeRange(entry.breakFrom, entry.breakTo)})` : '';
        return {
            kind: 'custom',
            status,
            text: `${formatTimeRange(entry.open, entry.close)}${breakText}`,
            ranges,
            open: toTime(entry.open),
            close: toTime(entry.close),
            ...breakOf(entry)
        };
    }
    return { kind: 'normal', status: DAY_STATUS.NORMAL, text: 'Normal', ranges };
};

export const countWeekday = (from, to, day) => {
    if (!from || !to || to < from) return 0;
    const start = dayjs(from);
    const end = dayjs(to);
    const target = DAYS.indexOf(day);
    const first = start.add((target - start.day() + 7) % 7, 'day');
    if (first.isAfter(end)) return 0;
    return Math.floor(end.diff(first, 'day') / 7) + 1;
};

export const isRangeExpired = (range) => !!range.to && range.to < dayjs().format('YYYY-MM-DD');


export const needsTimes = (status) => status === DAY_STATUS.CUSTOM;

export const needsDates = (status) => status === DAY_STATUS.CUSTOM_OFF;

const timeError = (rule) => {
    if (!needsTimes(rule.status)) return null;
    if (!rule.open || !rule.close) return 'Open and Close must both be set';
    if (rule.open >= rule.close) return 'Open time must be before Close time';
    if (rule.hasBreak) {
        if (!rule.breakFrom || !rule.breakTo) return 'Pick when the break starts and ends, or switch off the break';
        if (rule.breakFrom >= rule.breakTo) return 'Break must start before it ends';
        if (rule.breakFrom <= rule.open || rule.breakTo >= rule.close) return 'Break must be inside the opening hours';
    }
    return null;
};

const dateRangeError = (day, range, others) => {
    if (!range.from || !range.to) return 'Pick the From and To dates';
    if (range.to < range.from) return 'To date must be on or after From date';
    if (range.to < dayjs().format('YYYY-MM-DD')) return 'This date range has ended. Remove it to save.';
    if (!countWeekday(range.from, range.to, day)) return `This date range has no ${day}`;
    const clash = others.find(other => other.from && other.to && other.from <= range.to && other.to >= range.from);
    return clash ? `Overlaps ${formatDate(clash.from)} – ${formatDate(clash.to)}` : null;
};

export const rangeKey = (day, range) => `${day}#${range.id}`;

export const validateSchedule = (rows) => {
    const errors = {};
    rows.forEach(row => {
        const ranges = row.ranges || [];
        const rowRange = needsDates(row.status) ? [{ id: '__row', from: row.from, to: row.to }] : [];

        const rowError = needsDates(row.status) ? dateRangeError(row.day, row, ranges) : timeError(row);
        if (rowError) errors[row.day] = rowError;

        ranges.forEach(range => {
            const key = rangeKey(row.day, range);
            const others = [...rowRange, ...ranges.filter(other => other.id !== range.id)];
            const dateMessage = dateRangeError(row.day, range, others);
            const message = dateMessage || timeError(range);
            if (message) {
                errors[key] = message;
                errors[`${key}@field`] = dateMessage ? 'dates' : 'time';
            }
        });
    });
    return errors;
};

export const validateSettings = (values) => {
    const errors = {};
    const isInt = (v) => Number.isInteger(v);
    const { maxAdvanceDays, minLeadMinutes, slotMinutes, slotMaxSeats } = values;
    if (maxAdvanceDays !== null && (!isInt(maxAdvanceDays) || maxAdvanceDays < 1 || maxAdvanceDays > 365)) {
        errors.maxAdvanceDays = 'MaxAdvanceDays must be between 1 and 365';
    }
    if (minLeadMinutes !== null && (!isInt(minLeadMinutes) || minLeadMinutes < 0 || minLeadMinutes > 1440)) {
        errors.minLeadMinutes = 'MinLeadMinutes must be between 0 and 1440';
    }
    if (slotMinutes !== null && (!isInt(slotMinutes) || slotMinutes <= 0)) {
        errors.slotMinutes = 'SlotMinutes must be greater than zero';
    }
    if (slotMaxSeats !== null && (!isInt(slotMaxSeats) || slotMaxSeats <= 0)) {
        errors.slotMaxSeats = 'SlotMaxSeats must be greater than zero';
    }
    return errors;
};

export const validateDateOverride = (body, wholeDay) => {
    const errors = {};
    if (!body.title) errors.title = 'Title is required';
    if (![DATE_OVERRIDE_TYPE.CLOSED, DATE_OVERRIDE_TYPE.OPEN].includes(body.type)) errors.type = 'Type must be CLOSED or OPEN';
    if (!body.fromDate) {
        errors.fromDate = 'From date is required';
    } else {
        const toDate = body.toDate || body.fromDate;
        if (toDate < body.fromDate) errors.toDate = 'ToDate must be on or after FromDate';
        else if (body.isRecurringYearly && dayjs(toDate).diff(dayjs(body.fromDate), 'day') + 1 > 31) {
            errors.toDate = 'A yearly recurring override cannot be longer than 31 days';
        }
    }
    if (!wholeDay) {
        if (!body.fromTime || !body.toTime) errors.time = 'Enter both From time and To time, or switch on Whole day';
        else if (body.fromTime >= body.toTime) errors.time = 'FromTime must be before ToTime';
    }
    return errors;
};

export const hasErrors = (errors) => Object.keys(errors || {}).length > 0;


export const dateOverrideLabel = (item) => {
    const hasTime = !!(item.fromTime || item.toTime);
    if (item.type === DATE_OVERRIDE_TYPE.CLOSED) return hasTime ? 'Partial closure' : 'Holiday';
    return hasTime ? 'Special opening (hours)' : 'Special opening (full day)';
};


export const COUNTER_GENDERS = [
    { value: 'MALE', label: 'Male', pill: 'is-primary' },
    { value: 'FEMALE', label: 'Female', pill: 'is-holiday' },
    { value: 'BOTH', label: 'Both', pill: 'is-open' }
];

export const counterGenderLabel = (value) => COUNTER_GENDERS.find(g => g.value === value)?.label || value || '-';

export const validateCounter = (form) => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Counter name is required';
    if (!COUNTER_GENDERS.some(g => g.value === form.gender)) errors.gender = 'Pick who this counter serves';
    if (!form.startTime || !form.endTime) errors.hours = 'Pick the start and end time';
    else if (form.startTime >= form.endTime) errors.hours = 'Start time must be before end time';
    if (form.hasBreak) {
        if (!form.breakFrom || !form.breakTo) errors.break = 'Pick when the break starts and ends, or switch off the break';
        else if (form.breakFrom >= form.breakTo) errors.break = 'Break must start before it ends';
        else if (!errors.hours && (form.breakFrom <= form.startTime || form.breakTo >= form.endTime)) {
            errors.break = 'Break must be inside the counter hours';
        }
    }
    return errors;
};

export const counterSeatsPerStep = (counters, steps, slotMinutes) => steps.map(start => {
    const end = start + slotMinutes;
    return counters.filter(counter => {
        if (!counter.isActive) return false;
        const open = toMinutes(counter.startTime);
        const close = toMinutes(counter.endTime);
        if (open === null || close === null || open > start || close < end) return false;
        const breakFrom = toMinutes(counter.breakFrom);
        const breakTo = toMinutes(counter.breakTo);
        return !(breakFrom !== null && breakTo !== null && breakFrom < end && breakTo > start);
    }).length;
});

export const counterFieldFromMessage = (message) => {
    const text = message || '';
    if (/name|already exists/i.test(text) && !/center not found/i.test(text)) return 'name';
    if (/gender/i.test(text)) return 'gender';
    if (/break/i.test(text)) return 'break';
    if (/starttime|endtime/i.test(text)) return 'hours';
    return null;
};
